<template>
    <UiTeleport>
        <div class="private-page-scroller">
            <canvas
                ref="canvas"
                class="_dblock"
            />
            <div
                class="private-page-scroller__button"
                :class="{
                    'private-page-scroller__button_bottom': !allowTop,
                }"
                @click="onClick"
            >
                <div class="ico ico-arrow-left ico_color_white ico_size_32"></div>
            </div>
        </div>
    </UiTeleport>
</template>
<script>
import UiTeleport from '../ui/UiTeleport.vue';
import { computed, watch, onMounted, shallowRef } from 'vue';
import { useStore } from '../../store';
import { useRafFps } from '../../assets/js/vue/utlis/useRafFps';
import { scrollToElem } from '../../assets/js/utils';

export default {
    name: 'PrivatePageScroller',
    components: {
        UiTeleport,
    },
    setup() {
        const width = 140;
        const height = 140;
        let ctx = shallowRef();
        const canvas = shallowRef(null);
        let scale = 1;
        const store = useStore();
        const isMobile = computed(() => store.state.app.isMobile);
        const innerWidth = computed(() => store.state.app.windowWidth);
        const windowHeight = computed(() => store.state.app.windowHeight);
        const pageHeight = computed(() => store.state.app.pageHeight);
        const scrollY = computed(() => store.state.app.scrollY);
        let track1 = 0;
        const speed1 = 12; // loop animation length in sec
        const fps = 60;
        const progress = computed(() => scrollY.value / (pageHeight.value - windowHeight.value));

        const OrbitsParams = (scale) => ({
            r1: 36* scale,
            r2: 65* scale,
            x0: 70* scale,
            y0: 70* scale,
            endAngle: 2 * Math.PI,
        });
        let orbitsParams = OrbitsParams(scale);

        const PlanetParams = (scale) => ({
            r: 36,
            r1: 2 * scale,
            x0: 70,
            y0: 70,
            endAngle: 2 * Math.PI,
        });
        let planetParams = PlanetParams(scale);

        const ProgressParams = (scale) => ({
            r: 65,
            r1: 4 * scale,
            x0: 70,
            y0: 70,
            endAngle: 2 * Math.PI,
        });
        let progressParams = ProgressParams(scale);

        const ProgressValue = (value, scale, progressParams) => {
            const deg = (360 * (1 - value) + 90 + 360) % 360;

            return {
                x: (Math.cos(deg*Math.PI/180) * progressParams.r + progressParams.x0)*scale,
                y: (Math.sin(-deg*Math.PI/180) * progressParams.r + progressParams.y0)*scale,
            };
        };
        let progressValue = ProgressValue(progress.value, scale, progressParams);

        watch([innerWidth, ctx], ([innerWidth, ctx]) => {
            if (!ctx) return;

            scale = innerWidth / 1920;

            ctx.canvas.width = width * scale;
            ctx.canvas.height = height * scale;

            orbitsParams = OrbitsParams(scale);
            planetParams = PlanetParams(scale);
            progressParams = ProgressParams(scale);
        }, { immediate: true });

        watch(progress, (val) => {
            progressValue = ProgressValue(val, scale, progressParams);
        });

        const drawPlanet = () => {
            const deg = 360 * (1 - track1);

            const x = (Math.cos(deg*Math.PI/180) * planetParams.r + planetParams.x0)*scale;
            const y = (Math.sin(-deg*Math.PI/180) * planetParams.r + planetParams.y0)*scale;

            ctx.value.beginPath();
            ctx.value.arc(x, y, planetParams.r1, 0, planetParams.endAngle);
            ctx.value.fillStyle = '#6B6B6B';
            ctx.value.fill();
        };
        const drawOrbits = () => {
            ctx.value.beginPath();
            ctx.value.arc(orbitsParams.x0, orbitsParams.y0, orbitsParams.r1, 0, orbitsParams.endAngle);
            ctx.value.strokeStyle = '#6B6B6B';
            ctx.value.lineWidth = 1.2;
            ctx.value.stroke();

            ctx.value.beginPath();
            ctx.value.arc(orbitsParams.x0, orbitsParams.y0, orbitsParams.r2, 0, orbitsParams.endAngle);
            ctx.value.strokeStyle = '#6B6B6B';
            ctx.value.lineWidth = 1.2;
            ctx.value.stroke();
        }

        const drawProgress = () => {
            ctx.value.beginPath();
            ctx.value.arc(progressValue.x, progressValue.y, progressParams.r1, 0, progressParams.endAngle);
            ctx.value.fillStyle = '#D9D9D9';
            ctx.value.fill();
        };

        const draw = () => {
            ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
            if (track1 < 1) track1 = Math.min(track1 + 1/speed1/fps, 1); else track1 = 0;

            drawOrbits();
            drawPlanet();
            drawProgress();
        };

        const init = () => {
            ctx.value = canvas.value.getContext('2d');
        };

        const pause = computed(() => isMobile.value);
        useRafFps(() => {
            draw();
        }, pause, fps);

        onMounted(() => {
            init();
        });

        const allowTop = computed(() => {
            return scrollY.value >= windowHeight.value;
        });

        const onClick = () => {
            if (allowTop.value) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            } else {
                if (isMobile.value) {
                    scrollToElem(document.querySelector('[id=private-products]'));
                } else {
                    scrollToElem(document.querySelector('[id=private-products-deposits]'));
                }
            }
        };

        return {
            canvas,
            progress,
            allowTop,
            onClick,
        };
    },
};
</script>
<style lang="scss">
@import '../../assets/scss/base/variables';
@import '../../assets/scss/base/functions';
@import '../../assets/scss/base/mixins';

.private-page-scroller {
    position: fixed;
    z-index: 1;
    @include fluid('bottom', 108px);
    @include fluid('right', 32px);
    @include mobile {
        display: none;
    }

    &__button {
        --private-page-scroller__button_r: 90deg;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotateZ(var(--private-page-scroller__button_r));
        transition: .2s transform;
        cursor: pointer;

        &_bottom {
            --private-page-scroller__button_r: -90deg;
        }
    }
}
</style>
